import React, { useState, useEffect } from 'react';
import { AppBar, Box, Drawer, Toolbar, Typography, Button, IconButton } from '@mui/material';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import Sidebar from './components/Sidebar';
import MenuIcon from '@mui/icons-material/Menu';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { PieChart, Pie, Cell, Legend, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TaskData } from './TaskData';

const drawerWidth = 240;
const drawerMiniWidth = 60;

const Home: React.FC = () => {
  const [open, setOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [markdownCount, setMarkdownCount] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, signOut } = useAuth();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // スマホ判定

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    const saved = localStorage.getItem('markdown-list');
    if (saved) {
      const list = JSON.parse(saved);
      setMarkdownCount(list.length);
    }
  }, []);

  const handleLogout = () => {
    signOut();
    navigate('/signin');
  };

  const isHome = location.pathname === '/';

  // 状態ごとのカラー設定
const COLORS = ['#8884d8', '#82ca9d', '#ffc658'];

const [taskStats, setTaskStats] = useState<{ name: string; value: number }[]>([]);

useEffect(() => {
  const savedTasks = localStorage.getItem('task-list');
  if (savedTasks) {
    const taskList: TaskData[] = JSON.parse(savedTasks);
    const stats = { '未着手': 0, '進行中': 0, '完了': 0 };
    taskList.forEach(task => {
      if (task.status === '進行中') stats['進行中']++;
      else if (task.status === '完了') stats['完了']++;
      else stats['未着手']++;
    });
    setTaskStats(Object.entries(stats).map(([name, value]) => ({ name, value })));
  }
}, []);

const [taskTrend, setTaskTrend] = useState<{ date: string; count: number }[]>([]);

useEffect(() => {
  const savedTasks = localStorage.getItem('task-list');
  if (savedTasks) {
    const taskList: TaskData[] = JSON.parse(savedTasks);

    // 日ごとのタスク数を集計
    const countByDate: Record<string, number> = {};
    taskList.forEach(task => {
      const date = new Date(task.createdAt).toLocaleDateString(); // 登録日時から日付を取得
      countByDate[date] = (countByDate[date] || 0) + 1;
    });

    // 日付順に並び替え
    const sortedDates = Object.keys(countByDate).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

    setTaskTrend(sortedDates.map(date => ({ date, count: countByDate[date] })));
  }
}, []);

  return (
    <Box sx={{ display: 'flex' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${open ? drawerWidth : drawerMiniWidth}px)` },
          ml: { sm: `${open ? drawerWidth : drawerMiniWidth}px` },
          transition: 'width 0.3s, margin 0.3s',
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Magic Scroll
          </Typography>
          {currentUser && (
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body1">こんにちは、{currentUser.name}さん</Typography>
              <Button color="inherit" onClick={handleLogout}>
                ログアウト
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: open ? drawerWidth : drawerMiniWidth }, flexShrink: { sm: 0 } }}
        aria-label="sidebar navigation"
      >
        {/* モバイル用 Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          <Sidebar open={true} setOpen={() => {}} /> {/* モバイルは常にフルオープン */}
        </Drawer>

        {/* PC用 Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              width: open ? drawerWidth : drawerMiniWidth,
              transition: 'width 0.3s',
              boxSizing: 'border-box',
            },
          }}
          open
        >
          <Toolbar />
          <Sidebar open={open} setOpen={setOpen} />
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${open ? drawerWidth : drawerMiniWidth}px)` },
        }}
      >
        <Toolbar />
        {isHome && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              ダッシュボード
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ minWidth: 200, p: 2, boxShadow: 3 }}>
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                  登録Markdown数
                </Typography>
                <Typography variant="h4">{markdownCount}</Typography>
              </Box>
              <Box sx={{ minWidth: 200, p: 2, boxShadow: 3 }}>
                <Typography variant="h6" gutterBottom>
                  タスク状態のサマリー
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      dataKey="value"
                      isAnimationActive={true}
                      data={taskStats}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label
                    >
                      {taskStats.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box sx={{ minWidth: 200, p: 2, boxShadow: 3 }}>
                <Typography variant="h6" gutterBottom>
                  タスク登録数推移
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={taskTrend}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="#8884d8" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Box>
          </Box>
        )}
        <Outlet />
      </Box>
    </Box>
  );
};

export default Home;
